import { Chart, ChartDataset, ChartDataPoint, Checkbox, ClickEvent, Panel, PanelProps, TreeNode, ChangeEvent } from "@mcleod/components";
import { AutogenLayoutMetricViewer } from "./autogen/AutogenLayoutMetricViewer";
import { Api, ModelRow } from "@mcleod/core";

export class MetricViewer extends AutogenLayoutMetricViewer {
    private categoriesData: any[] = [];
    private attributesByCategory: { [key: string]: any[] } = {};

    constructor(props: Partial<PanelProps>) {
        super(props);
            this.addMountListener(() => {
                this.ensureLoaded().then(() => {
                    this.loadAttributes();
                });
            });
    }

    /** This is an event handler for the onClick event of buttonLoadAttributes. */
    async loadAttributes() {
        this.treeMetricAttributes.getRootNode().removeAllChildren();

        const categories: any = await Api.search(this.sourceMetricCategory.url, {}, null);
        this.categoriesData = categories.data;

        for (const category of categories.data) {
            const categoryNode = this.treeMetricAttributes.getRootNode().addChild(new TreeNode({
                id: category.metric_category,
                text: category.metric_category,
                expanded: false
            }));

            // Search for attributes for this category
            const attributes: any = await Api.search(this.sourceMetricAttribute.url, {
                metric_category: category.metric_category
            }, null);

            // Store attributes for this category
            this.attributesByCategory[category.metric_category] = attributes.data;

            // Add each attribute as a subnode to the category node
            for (const attribute of attributes.data) {
                const checkbox=new Checkbox({ caption: attribute.metric_attribute || attribute.name, padding: 0, margin: 0 });
                checkbox.addChangeListener((e:ChangeEvent)=>{
                    this.buttonDisplayChartsOnClick(null);
                });

                categoryNode.addChild(new TreeNode({
                    id: attribute.id || attribute.metric_attribute,
                    component: checkbox,
                    expanded: false
                }));
            }
        }

        this.treeProcesses.getRootNode().removeAllChildren();

        // Query sourceMetricData with processes_only=true
        const processes: any = await Api.search(this.sourceMetricData.url, {
            processes_only: true
        }, null);

        // Group processes by class name
        const classesByName: { [key: string]: any[] } = {};
        for (const process of processes.data) {
            const className = process.applies_to_class || 'Unknown';
            if (!classesByName[className]) {
                classesByName[className] = [];
            }
            classesByName[className].push(process);
        }

        // Sort class names with specific order: InternalWebServer, McLeodWebServer, ExternalWebServer, then alphabetically
        const sortedClassNames = Object.keys(classesByName).sort((a, b) => {
            const order = ['InternalWebServer', 'McLeodWebServer', 'ExternalWebServer'];
            const indexA = order.indexOf(a);
            const indexB = order.indexOf(b);

            // If both are in the priority list, sort by their order
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }
            // If only a is in the priority list, it comes first
            if (indexA !== -1) {
                return -1;
            }
            // If only b is in the priority list, it comes first
            if (indexB !== -1) {
                return 1;
            }
            // Both are not in the priority list, sort alphabetically
            return a.localeCompare(b);
        });

        // Build tree with class names at top level
        for (const className of sortedClassNames) {
            const classNode = this.treeProcesses.getRootNode().addChild(new TreeNode({
                id: className,
                text: className,
                expanded: false
            }));

            // Add each process start time as a checkbox under the class name
            for (const process of classesByName[className]) {

                const checkbox = this.createMetricCheckbox(process.process_start_time || process.process_id);

                classNode.addChild(new TreeNode({
                    id: process.process_id || process.process_start_time,
                    component: checkbox,
                    expanded: false
                }));
            }
        }
    }

    private createMetricCheckbox(caption: string): Checkbox {
        const checkbox = new Checkbox({ caption, padding: 0, margin: 0 });
        checkbox.addChangeListener(() => {
            this.buttonDisplayChartsOnClick(null);
        });
        return checkbox;
    }


    async buttonDisplayChartsOnClick(event: ClickEvent) {
        const checkedAttributeNodes: TreeNode[] = [];
        const getAllNodes = (node: TreeNode) => {
            if (node.component instanceof Checkbox && node.component.checked) {
                checkedAttributeNodes.push(node);
            }
            for (const child of node.children) {
                getAllNodes(child);
            }
        };
        getAllNodes(this.treeMetricAttributes.getRootNode());

        const checkedProcessNodes: TreeNode[] = [];
        const getAllProcessNodes = (node: TreeNode) => {
            if (node.component instanceof Checkbox && node.component.checked) {
                checkedProcessNodes.push(node);
            }
            for (const child of node.children) {
                getAllProcessNodes(child);
            }
        };
        getAllProcessNodes(this.treeProcesses.getRootNode());

        const chartParent = this.panelCharts;

        chartParent.removeAll();

        // Group checked processes by class name
        const processesByClass: { [key: string]: TreeNode[] } = {};
        for (const processNode of checkedProcessNodes) {
            const className = processNode.parent?.id || 'Unknown';
            if (!processesByClass[className]) {
                processesByClass[className] = [];
            }
            processesByClass[className].push(processNode);
        }

        // make the individual charts larger if there are only one or two charts to display
        let width = "500";
        let height = "300";
        const totalCharts = checkedAttributeNodes.length * Object.keys(processesByClass).length;
        if( totalCharts === 2 ){
            width = "700";
            height = "400";
        } else if( totalCharts === 1 ){
            width = "900";
            height = "500";
        }

        for (let i = 0; i < checkedAttributeNodes.length; i++) {
            const node = checkedAttributeNodes[i];
            const checkbox = node.component as Checkbox;
            const attributeName = checkbox.caption;
            const metric_category = node.parent?.id;

            for (const className in processesByClass) {
                const processNodes = processesByClass[className];

                const newChart = new Chart({
                    type: "chart",
                    field: "metric_value",
                    id: "chartMetric-"+className+"-"+metric_category+"-"+attributeName,
                    datasetLabel: className+" - "+metric_category+" - "+attributeName,
                    xAxisField: "metric_time"
                });

                const allMetricDataRows: ModelRow[] = [];
                for (const processNode of processNodes) {
                    const processId = processNode.id;

                    const metricDataRows = (await Api.search(this.sourceMetricData.url,
                    {
                        "applies_to_class": className,
                        "process_id": processId,
                        "metric_category": metric_category,
                        "attribute_name": attributeName
                    }
                    )).data.map((row: any) => new ModelRow(this.sourceMetricData.url, true, row));

                    allMetricDataRows.push(...metricDataRows);
                }

                if (allMetricDataRows.length > 0) {
                    newChart.displayComponentData(allMetricDataRows[0], allMetricDataRows, 0);
                }

                const newChartPanel = new Panel({
                    id: "chartPanel-"+className+"-"+metric_category+"-"+attributeName,
                    width: width,
                    height: height,
                    rowBreak: false
                });
                newChartPanel.add(newChart);
                chartParent.add(newChartPanel);
            }
        }

    }

    textboxFilterAttributesOnChange(event: ChangeEvent) {
        const filterText = this.textboxFilterAttributes.text?.toLowerCase() || '';

        const checkedNodeIds = new Set<string>();
        const getAllCheckedNodes = (node: TreeNode) => {
            if (node.component instanceof Checkbox && node.component.checked) {
                checkedNodeIds.add(node.id);
            }
            for (const child of node.children) {
                getAllCheckedNodes(child);
            }
        };
        getAllCheckedNodes(this.treeMetricAttributes.getRootNode());

        this.treeMetricAttributes.getRootNode().removeAllChildren();

        for (const category of this.categoriesData) {
            const attributes = this.attributesByCategory[category.metric_category] || [];

            const filteredAttributes = attributes.filter((attribute: any) => {
                const attributeName = (attribute.metric_attribute || attribute.name || '').toLowerCase();
                const attributeId = attribute.id || attribute.metric_attribute;
                return attributeName.includes(filterText) || checkedNodeIds.has(attributeId);
            });

            if (filteredAttributes.length > 0) {
                const categoryNode = this.treeMetricAttributes.getRootNode().addChild(new TreeNode({
                    id: category.metric_category,
                    text: category.metric_category,
                    expanded: true
                }));

                for (const attribute of filteredAttributes) {
                    const attributeId = attribute.id || attribute.metric_attribute;
                    const checkbox = new Checkbox({
                        caption: attribute.metric_attribute || attribute.name,
                        padding: 0,
                        margin: 0,
                        checked: checkedNodeIds.has(attributeId)
                    });
                    checkbox.addChangeListener((e: any) => {
                        this.buttonDisplayChartsOnClick(null);
                    });

                    categoryNode.addChild(new TreeNode({
                        id: attributeId,
                        component: checkbox,
                        expanded: false
                    }));
                }
            }
        }
    }
}
